import {browserSessionStorage} from "core/browserStorage";
import {readReferralCodeFromQuery} from "./referralAttribution";

/**
 * When the browser owes the funnel one of the two invite steps it can witness
 * (kitecorp/transferify-api-service#1582).
 *
 * <b>Why these two and not the third.</b> `INVITE_SIGNED_UP` is published by the api from inside the
 * transaction that writes the referral edge, so it counts exactly what the `referrals` table holds. The
 * other two leave no server trace at all: a copied link is a clipboard write, and an arrival on a
 * `?ref=` URL is a page load the api never sees as an invite. Until they are reported the funnel shows
 * joins with no denominator, which reads as if invites are never sent.
 *
 * <b>The denominator is invites, never readers.</b> These steps ride `REFERRAL_TRIAL` because it is the
 * same loop and one dashboard reads one surface, but the card steps on that moment (`shown`,
 * `dismissed`) count a referred friend meeting an offer. Averaging an invite step with a card step
 * averages two different populations.
 *
 * React-free by mandate, and telemetry-free with it: this module decides, `referral/useInviteShare.ts`
 * and `referral/useReferralAttribution.ts` are the adapters that name the emitter, and nothing here
 * knows that anyone is listening (owner 2026-08-27).
 */

/**
 * Whether a press of a referrer's share control actually handed an invite out.
 *
 * <b>A failed copy is not an invite sent.</b> `home/components/shareLink.copyText` resolves false when
 * neither the async clipboard API nor the `execCommand` fallback worked — a denied permission, a
 * non-secure context, a browser with neither — and on those presses the reader has nothing to paste, so
 * nobody was invited. Reporting it anyway would inflate the denominator that `invite_opened` and
 * `invite_signed_up` are both read against, which is the one number this step exists to supply.
 *
 * The press is otherwise NOT deduplicated, deliberately: a referrer who copies the link twice and sends
 * it to two people invited twice, and a latch would count one of those two.
 */
export function inviteSentByCopy(copied: boolean): boolean {
	return copied;
}

/** Versioned like every other stored offer fact, so a future shape change invalidates rather than misreads. */
export const OPENED_INVITES_SESSION_STORAGE_KEY = "referral.invite.opened.v1";

/**
 * How many invites one visit remembers having reported. Past it the oldest entry is evicted, which at
 * worst counts one opening twice for somebody who followed more than this many different links in one
 * tab — a case that does not otherwise occur.
 */
const MAX_REMEMBERED_INVITES = 20;

/**
 * Whether this landing opened an invite this visit has not already reported — and records it when so.
 *
 * <b>The code has to be one the app's grammar admits.</b> `readReferralCodeFromQuery` shares its
 * pattern with the api's `ReferralCodes.CODE_PATTERN`, so a `?ref=` the api would never attribute is
 * not an opened invite either: counting it would put arrivals on mistyped and crawled URLs into the
 * middle of the referral funnel.
 *
 * <b>SESSION storage, which is the scope "one landing" actually means.</b> The latch has to survive a
 * RELOAD — pressing refresh on a share link is not a second opening of that invite — and `OFFER_SHOWN`'s
 * in-memory `createOfferShownLog` cannot do that, because a reload is a new document and takes every
 * per-mount latch with it. It is the same per-visit scope, the same store and the same trade
 * `offers/ladder/ladderVisit.ts` already documents: narrower than the word "session" because
 * `sessionStorage` is per TAB, and correct in the direction that matters, since a reader who comes back
 * next week on the same link has genuinely opened that invite again and should be counted again.
 *
 * <b>Recorded BEFORE the report rather than after it.</b> The report is fire-and-forget and its outcome
 * is never known, so waiting for one would mean waiting forever; and a blocked store (private mode, a
 * quota) simply leaves the visit undeduplicated rather than silently reporting nothing, which is the
 * failure a funnel can read and recover from.
 *
 * @param search a `location.search` value, with or without the leading `?`
 */
export function inviteOpenedOnLanding(search: string): boolean {
	const code = readReferralCodeFromQuery(search);
	if (code === null) {
		return false;
	}
	const reported = openedInvites();
	if (reported.includes(code)) {
		return false;
	}
	rememberOpenedInvite(reported, code);
	return true;
}

/** The invites this visit has already reported, in the order they were opened. */
function openedInvites(): string[] {
	const storage = browserSessionStorage();
	if (storage === null) {
		return [];
	}
	try {
		const parsed: unknown = JSON.parse(storage.getItem(OPENED_INVITES_SESSION_STORAGE_KEY) ?? "[]");
		return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
	} catch {
		return [];
	}
}

function rememberOpenedInvite(reported: readonly string[], code: string): void {
	const storage = browserSessionStorage();
	if (storage === null) {
		return;
	}
	try {
		storage.setItem(
			OPENED_INVITES_SESSION_STORAGE_KEY,
			JSON.stringify([...reported, code].slice(-MAX_REMEMBERED_INVITES)),
		);
	} catch {
		// A full or blocked store costs this visit its reload dedup and nothing else. It must never cost
		// the reader their landing, and it must never suppress the report: an opening nobody recorded is
		// still an opening.
	}
}
